import React, { useCallback, useEffect, useState } from "react";
import { GetRequest } from "../../../api/Request";
import {
  CAMPAIGN_LABEL,
  CAMPAIGN_SCAN_MILESTONE_STEP,
  CampaignEntry,
  CampaignEnum,
  CampaignSummary,
} from "./campaign.types";
import CampaignEntryTable from "./CampaignEntryTable";
import "./styles/campaignDashboard.scss";

const ENTRY_PAGE_LIMIT = 35;

const formatNumber = (value: number) =>
  Number(value || 0).toLocaleString("en-IN");

const formatDateTime = (value?: string) =>
  value
    ? new Date(value).toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "-";

const CampaignDashboard: React.FC = () => {
  const [campaign, setCampaign] = useState<CampaignEnum>(CampaignEnum.Garba);
  const [refreshKey, setRefreshKey] = useState(0);
  // Garba has its own counters view; every other campaign shows the generic entry table
  const isLeadCampaign = campaign !== CampaignEnum.Garba;
  const [summary, setSummary] = useState<CampaignSummary | null>(null);
  const [entryList, setEntryList] = useState<CampaignEntry[]>([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [entryLoading, setEntryLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSummary = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await GetRequest(`admin/v1/campaign/${campaign}`);
      setSummary(response.data);
    } catch (err: any) {
      setError(err?.message || "Failed to fetch campaign counts");
    } finally {
      setLoading(false);
    }
  }, [campaign]);

  const fetchEntryList = useCallback(
    async (pageNumber: number) => {
      try {
        setEntryLoading(true);
        const response = await GetRequest(
          `admin/v1/campaign/${campaign}/entry?page=${pageNumber}&limit=${ENTRY_PAGE_LIMIT}`,
        );
        setEntryList(response.data || []);
      } catch (err: any) {
        setError(err?.message || "Failed to fetch giveaway entries");
      } finally {
        setEntryLoading(false);
      }
    },
    [campaign],
  );

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    if (!isLeadCampaign) fetchEntryList(page);
  }, [fetchEntryList, page, isLeadCampaign]);

  const handleRefresh = () => {
    fetchSummary();
    if (isLeadCampaign) setRefreshKey((key) => key + 1);
    else fetchEntryList(page);
  };

  const handleCampaignChange = (next: CampaignEnum) => {
    if (next === campaign) return;
    setSummary(null);
    setEntryList([]);
    setPage(1);
    setCampaign(next);
  };

  const campaignSwitch = (
    <div className="campaign-dashboard__switch" role="tablist">
      {Object.values(CampaignEnum).map((name) => (
        <button
          key={name}
          role="tab"
          aria-selected={name === campaign}
          className={name === campaign ? "is-active" : ""}
          onClick={() => handleCampaignChange(name)}
        >
          {CAMPAIGN_LABEL[name]}
        </button>
      ))}
    </div>
  );

  if (loading && !summary) {
    return (
      <div className="campaign-dashboard">
        {campaignSwitch}
        <div className="campaign-dashboard__loading">Loading campaign...</div>
      </div>
    );
  }

  if (error && !summary) {
    return (
      <div className="campaign-dashboard">
        {campaignSwitch}
        <div className="campaign-dashboard__error">{error}</div>
      </div>
    );
  }

  if (!summary) return null;

  if (isLeadCampaign) {
    return (
      <div className="campaign-dashboard">
        {campaignSwitch}
        <div className="campaign-dashboard__header">
          <div>
            <h1>{CAMPAIGN_LABEL[campaign]} Campaign</h1>
            <p>Entries with the details the campaign page saved</p>
          </div>
          <button onClick={handleRefresh} disabled={loading}>
            Refresh
          </button>
        </div>
        <CampaignEntryTable
          campaign={campaign}
          entryCount={summary.entryCount}
          refreshKey={refreshKey}
        />
      </div>
    );
  }

  const sourceList = Object.entries(summary.scanSourceCount || {}).sort(
    (a, b) => b[1] - a[1],
  );
  const nextMilestone =
    (Math.floor(summary.scanCount / CAMPAIGN_SCAN_MILESTONE_STEP) + 1) *
    CAMPAIGN_SCAN_MILESTONE_STEP;
  const milestoneProgress =
    ((summary.scanCount % CAMPAIGN_SCAN_MILESTONE_STEP) /
      CAMPAIGN_SCAN_MILESTONE_STEP) *
    100;

  return (
    <div className="campaign-dashboard">
      {campaignSwitch}
      <div className="campaign-dashboard__header">
        <div>
          <h1>{summary.name} Campaign</h1>
          <p>
            QR scans, giveaway entries, votes and shares for
            modulusseventeen.com/{summary.name.toLowerCase()}
          </p>
        </div>
        <button onClick={handleRefresh} disabled={loading || entryLoading}>
          Refresh
        </button>
      </div>

      <div className="campaign-dashboard__stats">
        <div className="campaign-dashboard__stat-card campaign-dashboard__stat-card--primary">
          <div className="stat-label">QR Scans</div>
          <div className="stat-value">{formatNumber(summary.scanCount)}</div>
          <div className="stat-subtitle">All sources together</div>
        </div>
        <div className="campaign-dashboard__stat-card campaign-dashboard__stat-card--success">
          <div className="stat-label">Giveaway Entries</div>
          <div className="stat-value">{formatNumber(summary.entryCount)}</div>
          <div className="stat-subtitle">
            {summary.scanCount > 0
              ? Math.round((summary.entryCount / summary.scanCount) * 100)
              : 0}
            % of scans entered
          </div>
        </div>
        <div className="campaign-dashboard__stat-card campaign-dashboard__stat-card--info">
          <div className="stat-label">Likes</div>
          <div className="stat-value">{formatNumber(summary.likeCount)}</div>
          <div className="stat-subtitle">Partner reveal video</div>
        </div>
        <div className="campaign-dashboard__stat-card campaign-dashboard__stat-card--danger">
          <div className="stat-label">Dislikes</div>
          <div className="stat-value">{formatNumber(summary.dislikeCount)}</div>
          <div className="stat-subtitle">Takedown meter votes</div>
        </div>
        <div className="campaign-dashboard__stat-card campaign-dashboard__stat-card--warning">
          <div className="stat-label">Shares</div>
          <div className="stat-value">{formatNumber(summary.shareCount)}</div>
          <div className="stat-subtitle">Share button taps</div>
        </div>
        <div className="campaign-dashboard__stat-card campaign-dashboard__stat-card--muted">
          <div className="stat-label">Blocked Repeat Entries</div>
          <div className="stat-value">
            {formatNumber(summary.blockedEntryCount)}
          </div>
          <div className="stat-subtitle">Same phone number or device</div>
        </div>
      </div>

      <div className="campaign-dashboard__panel">
        <h2>Scans by QR source</h2>
        <div className="milestone">
          <div className="milestone__row">
            <span>
              Next alert email at <b>{formatNumber(nextMilestone)}</b> scans
            </span>
            <span>{formatNumber(nextMilestone - summary.scanCount)} to go</span>
          </div>
          <div className="milestone__track">
            <span style={{ width: `${milestoneProgress}%` }} />
          </div>
          <div className="milestone__note">
            Last alert sent at:{" "}
            {summary.lastScanMilestone > 0
              ? `${formatNumber(summary.lastScanMilestone)} scans`
              : "not yet"}
          </div>
        </div>

        {sourceList.length === 0 ? (
          <div className="no-data">No scans yet.</div>
        ) : (
          <table className="campaign-dashboard__table">
            <thead>
              <tr>
                <th>Source</th>
                <th className="num">Scans</th>
                <th className="num">Share of total</th>
              </tr>
            </thead>
            <tbody>
              {sourceList.map(([source, count]) => (
                <tr key={source}>
                  <td>
                    <span className="source-chip">{source}</span>
                  </td>
                  <td className="num">{formatNumber(count)}</td>
                  <td className="num">
                    {summary.scanCount > 0
                      ? Math.round((count / summary.scanCount) * 100)
                      : 0}
                    %
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="campaign-dashboard__panel">
        <h2>Giveaway entries</h2>
        {entryLoading && entryList.length === 0 ? (
          <div className="no-data">Loading entries...</div>
        ) : entryList.length === 0 ? (
          <div className="no-data">
            {page > 1 ? "No more entries." : "No entries yet."}
          </div>
        ) : (
          <div className="table-scroll">
            <table className="campaign-dashboard__table">
              <thead>
                <tr>
                  <th>Entry No.</th>
                  <th>Name</th>
                  <th>Phone</th>
                  <th>Source</th>
                  <th>Entered at</th>
                </tr>
              </thead>
              <tbody>
                {entryList.map((entry) => (
                  <tr key={entry._id || entry.entryNumber}>
                    <td>
                      <b>{entry.entryNumber}</b>
                    </td>
                    <td>{entry.name}</td>
                    <td>{entry.phoneNumber || "-"}</td>
                    <td>
                      {entry.source ? (
                        <span className="source-chip">{entry.source}</span>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td>{formatDateTime(entry.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="campaign-dashboard__pagination">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1 || entryLoading}
          >
            Previous
          </button>
          <span>Page {page}</span>
          <button
            onClick={() => setPage((p) => p + 1)}
            disabled={entryList.length < ENTRY_PAGE_LIMIT || entryLoading}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default CampaignDashboard;
